import { Button, Modal, useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  errorMessage,
  type OAuthClientCreated,
  type OAuthClientResponse,
  platformCreateOauthClientMutation,
  platformListOauthClientsQueryKey,
  platformUpdateOauthClientMutation,
} from "@/api";
import { useAppForm } from "@/components/form/form";
import { formValues, oauthClientSchema, toCreateBody, toUpdateBody } from "./oauthClientForm";

/**
 * Create (no `client`) or edit an OAuth client in a modal. Mount it only while
 * open (key it by client) so the form starts fresh each time.
 */
export function OAuthClientFormModal({
  client,
  onClose,
  onCreated,
}: Readonly<{ client?: OAuthClientResponse; onClose: () => void; onCreated: (created: OAuthClientCreated) => void }>) {
  const { t } = useTranslation("adminPlatform");
  const toast = useToast();
  const queryClient = useQueryClient();
  const create = useMutation(platformCreateOauthClientMutation());
  const update = useMutation(platformUpdateOauthClientMutation());
  const editing = client !== undefined;

  const form = useAppForm({
    defaultValues: formValues(client),
    validators: { onChange: oauthClientSchema(t("oauthClients.form.redirectsRequired")) },
    onSubmit: async ({ value }) => {
      try {
        if (client) {
          await update.mutateAsync({
            path: { client_uuid: client.uuid },
            body: toUpdateBody(value),
            throwOnError: true,
          });
          toast.success(t("oauthClients.form.saved", { name: value.clientName }));
        } else {
          const res = await create.mutateAsync({ body: toCreateBody(value), throwOnError: true });
          toast.success(t("oauthClients.form.created", { name: value.clientName }));
          onCreated(res.data);
        }
        await queryClient.invalidateQueries({ queryKey: platformListOauthClientsQueryKey() });
        onClose();
      } catch (error) {
        toast.error(
          errorMessage(error, t(editing ? "oauthClients.form.saveFailed" : "oauthClients.form.createFailed")),
        );
      }
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={
        editing ? t("oauthClients.form.editTitle", { name: client.clientName }) : t("oauthClients.form.createTitle")
      }
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("oauthClients.form.cancel")}
          </Button>
          <form.Subscribe selector={(s) => s.isSubmitting}>
            {(submitting) => (
              <Button variant="primary" loading={submitting} onClick={() => void form.handleSubmit()}>
                {editing ? t("oauthClients.form.save") : t("oauthClients.form.create")}
              </Button>
            )}
          </form.Subscribe>
        </>
      }
    >
      <form
        aria-label={
          editing ? t("oauthClients.form.editTitle", { name: client.clientName }) : t("oauthClients.form.createTitle")
        }
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
      >
        <form.AppField name="clientName">
          {(field) => (
            <field.TextField
              label={t("oauthClients.form.name")}
              required
              placeholder={t("oauthClients.form.namePlaceholder")}
            />
          )}
        </form.AppField>
        {!editing && (
          <form.AppField name="clientType">
            {(field) => (
              <field.SelectField
                label={t("oauthClients.form.type")}
                options={[
                  { value: "public", label: t("oauthClients.form.typePublic") },
                  { value: "confidential", label: t("oauthClients.form.typeConfidential") },
                ]}
              />
            )}
          </form.AppField>
        )}
        <form.AppField name="redirects">
          {(field) => (
            <field.TextareaField
              label={t("oauthClients.form.redirects")}
              required
              rows={3}
              placeholder={t("oauthClients.form.redirectsPlaceholder")}
            />
          )}
        </form.AppField>
        <form.AppField name="scopes">
          {(field) => <field.TextField label={t("oauthClients.form.scopes")} />}
        </form.AppField>
        <form.AppField name="requirePkce">
          {(field) => <field.ToggleField label={t("oauthClients.form.pkce")} />}
        </form.AppField>
      </form>
    </Modal>
  );
}
